/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useMemo } from 'react';
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import merge from 'deepmerge';
import { IncomingHttpHeaders } from 'http';
import fetch from 'isomorphic-unfetch';
import isEqual from 'lodash.isequal';
import type { AppProps } from 'next/app';

const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const createApolloClient = (headers: IncomingHttpHeaders | null = null) => {
    // isomorphic fetch for passing the cookies along with each GraphQL request
    const enhancedFetch = (url: RequestInfo, init: RequestInit) => {
        return fetch(url, {
            ...init,
            headers: {
                ...init.headers,
                'Access-Control-Allow-Origin': '*',
                Cookie: headers?.cookie ?? '',
            },
        }).then((response) => response);
    };

    return new ApolloClient({
        // SSR only for Node.js
        ssrMode: typeof window === 'undefined',
        link: ApolloLink.from([
            // this uses apollo-link-http under the hood, so all the options here come from that package
            createUploadLink({
                uri: `${process.env.NEXT_PUBLIC_STRAPI_BACKEND_URL}/graphql`,
                // Make sure that CORS and cookies work
                fetchOptions: {
                    mode: 'cors',
                },
                credentials: 'include',
                fetch: enhancedFetch,
                headers: {
                    Authorization: `Bearer ${process.env.STRAPI_READ_ONLY_KEY}`,
                },
            }),
        ]),
        cache: new InMemoryCache(),
        // SSG ISR bugfix apollo
        defaultOptions: {
            query: {
                fetchPolicy: 'no-cache',
            },
        },
    });
};

type InitialState = NormalizedCacheObject | undefined;

interface IInitializeApollo {
    headers?: IncomingHttpHeaders | null;
    initialState?: InitialState | null;
}

export const initializeApollo = (
    { headers, initialState }: IInitializeApollo = {
        headers: null,
        initialState: null,
    }
) => {
    const _apolloClient = apolloClient ?? createApolloClient(headers);

    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // get hydrated here
    if (initialState) {
        // Get existing cache, loaded during client side data fetching
        const existingCache = _apolloClient.extract();

        // Merge the existing cache into data passed from getStaticProps/getServerSideProps
        const data = merge(initialState, existingCache, {
            // combine arrays using object equality (like in sets)
            arrayMerge: (destinationArray, sourceArray) => [...sourceArray, ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s)))],
        });

        // Restore the cache with the merged data
        _apolloClient.cache.restore(data);
    }

    // For SSG and SSR always create a new Apollo Client
    if (typeof window === 'undefined') {
        return _apolloClient;
    }
    // Create the Apollo Client once in the client
    if (!apolloClient) {
        apolloClient = _apolloClient;
    }

    return _apolloClient;
};

export const addApolloState = (client: ApolloClient<NormalizedCacheObject>, pageProps: AppProps['pageProps']) => {
    if (pageProps?.props) {
        pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
    }

    return pageProps;
};

export function useApollo(pageProps: AppProps['pageProps']) {
    const state = pageProps[APOLLO_STATE_PROP_NAME];
    const store = useMemo(() => initializeApollo({ initialState: state }), [state]);
    return store;
}

export function flattenResponse(data: { [x: string]: { data: unknown } }) {
    const flattenedData: unknown[] = [];

    Object.keys(data).forEach((key) => {
        const { data: pageData } = data[key];
        Array.isArray(pageData) &&
            pageData.forEach((page) => {
                flattenedData.push({
                    ...page,
                    pageType: key,
                });
            });
    });

    return flattenedData;
}

const client = initializeApollo();
export default client;
